import {
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  inject,
  output
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

import { AuthService } from '@igo2/auth';
import { ConfigService } from '@igo2/core/config';
import { IgoLanguageModule } from '@igo2/core/language';

import { MSAL_GUARD_CONFIG } from '@azure/msal-angular';
import {
  AuthenticationResult,
  InteractionRequiredAuthError,
  InteractionStatus,
  PopupRequest,
  PublicClientApplication,
  SilentRequest
} from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import {
  AuthMicrosoftb2cOptions,
  MsalGuardConfigurationWithType
} from '../shared/auth-microsoft.interface';
import { MsalBroadcastServiceb2c } from './auth-msalBroadcastServiceb2c.service';
import { MsalServiceb2c } from './auth-msalServiceb2c.service';

@Component({
  selector: 'igo-auth-microsoftb2c',
  templateUrl: './auth-microsoftb2c.component.html',
  styleUrls: ['./auth-microsoftb2c.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, IgoLanguageModule, MatIconModule],
  providers: [MsalServiceb2c]
})
export class AuthMicrosoftb2cComponent {
  private authService = inject(AuthService);
  private config = inject(ConfigService);
  private appRef = inject(ApplicationRef);
  private msalService = inject(MsalServiceb2c);
  private msalGuardConfig =
    inject<MsalGuardConfigurationWithType[]>(MSAL_GUARD_CONFIG);

  private options: AuthMicrosoftb2cOptions;
  private readonly _destroying$ = new Subject<void>();
  readonly login = output<boolean>();
  private broadcastService: MsalBroadcastServiceb2c;

  constructor() {
    this.options = this.config.getConfig('auth.microsoftb2c') || {};

    this.msalService.instance = new PublicClientApplication({
      auth: this.options.browserAuthOptions,
      cache: {
        cacheLocation: 'sessionStorage'
      }
    });

    this.broadcastService = new MsalBroadcastServiceb2c();

    if (this.options.browserAuthOptions.clientId) {
      this.broadcastService.inProgress$
        .pipe(
          filter(
            (status: InteractionStatus) => status === InteractionStatus.None
          ),
          takeUntil(this._destroying$)
        )
        .subscribe(() => {
          this.checkAccount();
        });
    } else {
      console.warn('Microsoft authentification needs "clientId" option');
    }
  }

  public loginMicrosoftb2c() {
    this.msalService
      .loginPopup({ ...this.getConf().authRequest } as PopupRequest)
      .subscribe((response: AuthenticationResult) => {
        this.msalService.instance.setActiveAccount(response.account);
        this.checkAccount();
      });
  }

  private checkAccount() {
    this.msalService.instance
      .acquireTokenSilent(this.getConf().authRequest as SilentRequest)
      .then((response: AuthenticationResult) => {
        const token = response.idToken;
        this.authService.loginWithToken(token, 'microsoftb2c').subscribe(() => {
          this.appRef.tick();
          this.login.emit(true);
        });
      })
      .catch(async (error) => {
        if (error instanceof InteractionRequiredAuthError) {
          // fallback to interaction when silent call fails
          return this.msalService.acquireTokenPopup(
            this.getConf().authRequest as SilentRequest
          );
        }
      })
      .catch(() => {
        console.log('Silent token fails');
      });
  }

  private getConf(): MsalGuardConfigurationWithType {
    return this.msalGuardConfig.filter((conf) => conf.type === 'b2c')[0];
  }
}
